import { useCallback, useState } from 'react';
import { api } from '../lib/api';
import { formatMoney, formatDate } from '../lib/format';
import {
  useSyncList,
  SyncStateTabs,
  Pager,
  SyncStateBadge,
  ListStates,
  SortableTh,
} from './SyncStatusBrowser';
import {
  CustomerFilter,
} from './TransactionFilters';
import type { StatusOption } from './TransactionList';
import { ViewHeader } from './DashboardLayout';
import {
  useConnection,
  stripeSubscriptionUrl,
  hubspotDealUrl,
} from './links';
import { ExtLink } from './ExtLink';
import type { SubscriptionListEntry } from './types';

/**
 * Dashboard → Subscriptions (M10b + M19 T3).
 *
 * The subscription sync-status browser — the primary synced object. First-class
 * view (not nested under customers) because tenants with thousands of subs
 * can't navigate via customers.
 *
 * Filters: sync-state tabs, a Stripe `current_status` dropdown, the universal
 * Customer typeahead, and a Churn-watch (cancel-at-period-end) toggle. The
 * HubSpot Deal id is shown under the subscription id (linked) — consistent with
 * how refunds/disputes surface their parent charge id.
 *
 * Per-row actions (mirror the M9b Admin Panel):
 *   - Relink:    broken → recreate the Deal from current state → synced
 *   - Ignore:    broken → terminal escape (stop trying to sync this sub)
 *   - Re-enable: ignored → resume sync (creates a Deal if none exists) → synced
 *
 * Reference: docs/features/subscription-lifecycle-sync.md
 *            src/do/handlers/adminPanel.ts (relink/ignore/reEnable Subscription)
 */

/** Stripe `current_status` values (the raw column we filter server-side). */
const STATUS_OPTIONS: StatusOption[] = [
  { value: 'trialing', label: 'Trialing', tone: 'brand' },
  { value: 'active', label: 'Active', tone: 'success' },
  { value: 'past_due', label: 'Past due', tone: 'warn' },
  { value: 'canceled', label: 'Canceled', tone: 'neutral' },
  { value: 'incomplete', label: 'Incomplete', tone: 'neutral' },
  { value: 'incomplete_expired', label: 'Incomplete expired', tone: 'neutral' },
  { value: 'unpaid', label: 'Unpaid', tone: 'danger' },
];

export function Subscriptions() {
  // Stripe current_status filter (sent server-side via extraParams).
  const [status, setStatus] = useState('all');
  const list = useSyncList<SubscriptionListEntry>('admin/subscriptions', 25, {
    status: status !== 'all' ? status : undefined,
  });
  const {
    data,
    loading,
    error,
    refetch,
    state,
    setState,
    customer,
    setCustomer,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Subscriptions"
        subtitle="Stripe Subscription ↔ HubSpot Deal linkage state."
      />
      <SyncStateTabs state={state} setState={setState} hidePending />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
            Status
          </span>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="input max-w-[12rem] text-sm"
          >
            <option value="all">All</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Subscription</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <th className="px-4 py-2 font-medium">Products</th>
                  <SortableTh
                    label="MRR"
                    sortKey="mrr"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Renewal"
                    sortKey="renewal"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((s) => (
                  <SubscriptionRow key={s.stripeSubscriptionId} sub={s} onChanged={refetch} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function SubscriptionRow({
  sub,
  onChanged,
}: {
  sub: SubscriptionListEntry;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeSubscriptionUrl(
    stripeAccountId,
    sub.stripeSubscriptionId
  );
  // HubSpot Deal id shown under the subscription id (consistent with how
  // refunds/disputes surface the parent charge id), replacing the separate
  // HubSpot Deal column.
  const hsUrl = hubspotDealUrl(portalId, sub.hubspotDealId ?? '');

  const run = useCallback(
    async (op: 'relink' | 'ignore' | 're-enable', endpoint: string) => {
      setBusy(op);
      try {
        await api.post(endpoint, { stripeSubscriptionId: sub.stripeSubscriptionId });
        onChanged();
      } catch (err) {
        alert(
          `${op} failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setBusy(null);
      }
    },
    [sub.stripeSubscriptionId, onChanged]
  );

  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={sub.stripeSubscriptionId} title="View in Stripe" />
        ) : (
          <div className="font-mono text-xs text-ink-700">
            {sub.stripeSubscriptionId}
          </div>
        )}
        {sub.hubspotDealId ? (
          <div className="mt-0.5 font-mono text-xs text-ink-400">
            {hsUrl ? (
              <ExtLink href={hsUrl} label={sub.hubspotDealId} title="HubSpot Deal" />
            ) : (
              sub.hubspotDealId
            )}
          </div>
        ) : (
          <div className="mt-0.5 text-xs text-ink-400">no deal</div>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col">
          <span className="text-ink-800">{sub.customerName ?? '—'}</span>
          {sub.customerEmail && (
            <span className="text-xs text-ink-400">{sub.customerEmail}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 align-top text-xs text-ink-600">
        {sub.products.length > 0 ? (
          <ul className="flex max-w-[16rem] flex-col gap-0.5">
            {sub.products.map((p, i) => (
              <li key={`${p}-${i}`} className="truncate" title={p}>
                {p}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-ink-400">—</span>
        )}
        {sub.discount && (
          <span className="mt-0.5 block text-brand-600" title={sub.discount}>
            🏷 {sub.discount}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-ink-700 whitespace-nowrap">
        {formatMoney(sub.currentMrrMinor, sub.currency)}
        <span className="ml-1 text-xs text-ink-400">/{sub.currency}</span>
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={effectiveStatus(sub)} />
        {/* Pause subtext only when the EFFECTIVE status is 'paused' — NOT when
            the sub is already canceled. Stripe keeps `pause_collection` set
            even after a paused sub is canceled, so gating purely on
            pauseCollectionActive shows contradictory "canceled + paused"
            text. Wording matches Stripe ("Collection paused"). */}
        {sub.pauseCollectionActive && effectiveStatus(sub) === 'paused' && (
          <div className="mt-0.5 text-xs text-ink-400">
            {sub.pausedUntil
              ? `Collection paused · resumes ${formatDate(sub.pausedUntil)}`
              : 'Collection paused'}
          </div>
        )}
        {!sub.pauseCollectionActive &&
          sub.cancelAtPeriodEnd &&
          sub.currentPeriodEnd && (
            <div className="mt-0.5 text-xs text-ink-400">
              Cancels {formatDate(sub.currentPeriodEnd)}
            </div>
          )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500">
        {sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}
      </td>
      <td className="px-4 py-2">
        <SyncStateBadge state={sub.syncState} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1.5">
          {sub.syncState === 'broken' && (
            <>
              <button
                className="btn-ghost px-2 py-1 text-xs"
                disabled={!!busy}
                onClick={() => run('relink', 'admin/subscription/relink')}
              >
                {busy === 'relink' ? '…' : 'Relink'}
              </button>
              <button
                className="btn-ghost px-2 py-1 text-xs text-ink-400"
                disabled={!!busy}
                onClick={() => run('ignore', 'admin/subscription/ignore')}
              >
                {busy === 'ignore' ? '…' : 'Ignore'}
              </button>
            </>
          )}
          {sub.syncState === 'ignored' && (
            <button
              className="btn-ghost px-2 py-1 text-xs"
              disabled={!!busy}
              onClick={() => run('re-enable', 'admin/subscription/re-enable')}
            >
              {busy === 're-enable' ? '…' : 'Re-enable'}
            </button>
          )}
          {/* Outbound sub management (Pause/Resume/Cancel) lives in the HubSpot
              sidebar card — the sales rep's surface. This dashboard is the
              admin/sync-control view only. See hubspot-uiext SidebarCard.tsx. */}
          {sub.syncState === 'synced' && (
            <span className="text-xs text-ink-400">linked ✓</span>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Compute the *effective* display status from Stripe's independent state
 * dimensions. Stripe keeps `status='active'` during a pause
 * (`pause_collection`) and during a scheduled cancel (`cancel_at_period_end`)
 * — without this, a paused sub would show as plain "active" in the dashboard.
 *
 * Priority: canceled > paused > cancel-scheduled > raw status.
 */
function effectiveStatus(sub: SubscriptionListEntry): string {
  if (sub.canceledAt != null || sub.currentStatus === 'canceled') return 'canceled';
  if (sub.pauseCollectionActive) return 'paused';
  if (sub.cancelAtPeriodEnd) return 'canceling';
  return sub.currentStatus;
}

/** Color the Stripe subscription status. */
function StatusBadge({ status }: { status: string; }) {
  const cls =
    status === 'active' || status === 'trialing'
      ? 'bg-success-500/10 text-success-600'
      : status === 'past_due'
        ? 'bg-warn-500/10 text-warn-600'
        : status === 'canceled'
          ? 'bg-ink-100 text-ink-500'
          : status === 'paused'
            ? 'bg-warn-500/10 text-warn-600'
            : status === 'canceling'
              ? 'bg-warn-500/10 text-warn-600'
              : 'bg-ink-100 text-ink-600';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}
